import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLegalIdVerification, useVeriffEvents } from "../../hooks/legalIdVerification";
import styles from "./LegalIdVerification.module.scss";
import { logger } from "../../shared/utils/logger";
import { IntroScreen } from "./screens/IntroScreen";
import { ProcessingScreen } from "./screens/ProcessingScreen";
import { ConfirmationScreen } from "./screens/ConfirmationScreen";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { initAnalytics, setAnalyticsLocale, reportAnalyticsSetupFailure } from "../../shared/analytics/analyticsClient";
import { CaptureScreen } from "./screens/CaptureScreen";

/**
 * LegalIdVerification Component
 *
 * Handles the complete legal ID verification flow using Veriff SDK.
 * Mirrors FaceVerification.tsx architecture.
 *
 * States:
 * - initializing: Creating verification session with backend
 * - ready: Session ready, showing intro screen ("Take a photo of your ID")
 * - capturing: User clicked "Open camera", Veriff SDK is active
 * - processing: Veriff finished, polling backend for decision
 * - completed: Verification complete
 * - error: Verification failed
 */
export function LegalIdVerification() {
  const { t, i18n } = useTranslation();
  const { consumer, fragnoLogoEnabled } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const consumerLogo = consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  // Extract ISO 639-1 base code for Veriff (e.g. 'fr-CA' → 'fr', 'zh-Hans' → 'zh')
  const veriffLang = i18n.language.split('-')[0];
  const [showSpinner, setShowSpinner] = useState(true);
  const {
    state,
    error,
    verificationId,
    sessionToken,
    veriffSessionUrl,
    verificationResult,
    fallbackAvailable,
    setState,
    initializeLegalIdVerification,
    pollVerificationStatus,
    confirmVerification,
    handleVerificationError,
    requestHostReload,
  } = useLegalIdVerification();

  // Set up analytics transport + session once for this launch (identity bound later at
  // IDME_INIT, inside the hook). Runs before the parent handshake starts.
  useEffect(() => {
    try {
      initAnalytics({ module: "legal_id", provider: "veriff", locale: i18n.language });
    } catch (err) {
      logger.warn("[LegalIdVerification] analytics init failed; continuing without analytics", err);
      reportAnalyticsSetupFailure("init_failed", { module: "legal_id", error: err instanceof Error ? err.message : String(err) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The member can switch language mid-flow via the footer.
  useEffect(() => {
    setAnalyticsLocale(i18n.language);
  }, [i18n.language]);

  useVeriffEvents({
    veriffSessionUrl,
    state,
    verificationId,
    sessionToken,
    lang: veriffLang,
    setState,
    onVerificationFinished: pollVerificationStatus,
    onVerificationError: handleVerificationError,
    requestHostReload,
  });

  // Initialize on mount
  useEffect(() => {
    let cleanupFn: (() => void) | undefined;
    initializeLegalIdVerification().then((fn) => {
      cleanupFn = fn;
    });
    return () => {
      cleanupFn?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler: user clicks "Open camera" on the intro screen
  const handleOpenCamera = () => {
    logger.log("[LegalIdVerification] User clicked Open camera — starting Veriff");
    setState("capturing");
  };

  const handleFallback = () => {
    logger.log("[LegalIdVerification] User selected the host fallback");
    handleVerificationError("user_no_id", "User requested another identity verification method");
  };

  // Handler: toggle spinner visibility on the processing screen
  const handleHideAnimation = () => {
    setShowSpinner((prev) => !prev);
  };

  // Handler: user clicks "No, retake photo" on the confirmation screen
  // Sends retake request to parent — IVA owns retry limits and will restart the flow if allowed
  const handleRetake = () => {
    logger.log("[LegalIdVerification] User requested retake — notifying parent");
    handleVerificationError("user_retake_requested", "User requested to retake photo of ID", undefined, { uuid: verificationResult?.data?.uuid });
  };

  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(' ')}>
      <div className={styles.container}>

        {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
        {!fragnoLogoEnabled && (
          <header className={styles.logoContainer}>
            <div className={styles.logo}>
              {consumerLogo ? (
                <img src={consumerLogo.src} alt={consumerLogo.alt} />
              ) : (
                <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
              )}
            </div>
          </header>
        )}

        <main className={styles.contentContainer}>

          {/* Initializing — loading spinner while creating session */}
          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Ready — intro screen: "Take a photo of your ID" */}
          {state === "ready" && (
            <IntroScreen
              onOpenCamera={handleOpenCamera}
              onFallback={handleFallback}
              fallbackAvailable={fallbackAvailable}
              consumerLogo={consumerLogo}
              consumerConfig={consumerConfig}
            />
          )}

          {/* Capturing — mounted on every device so the document_capture_screen Viewed
              always fires. On desktop CaptureScreen renders #veriff-root for Veriff to
              mount into; on mobile Veriff draws its own modal and ignores the container,
              so CaptureScreen renders nothing (the check lives inside it). */}
          {state === "capturing" && veriffSessionUrl && (
            <CaptureScreen />
          )}

          {/* Opened the camera before the session finished creating */}
          {state === "capturing" && !veriffSessionUrl && (
            <div className={styles.card}>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Processing — polling backend for decision */}
          {state === "processing" && (
            <ProcessingScreen
              showSpinner={showSpinner}
              onHideAnimation={handleHideAnimation}
            />
          )}

          {/* Confirming — show extracted ID details for user to review */}
          {state === "confirming" && verificationResult && (
            <ConfirmationScreen
              verificationResult={verificationResult}
              onConfirm={confirmVerification}
              onRetake={handleRetake}
            />
          )}

          {/* Completed */}
          {state === "completed" && (
            <div className={styles.card}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.title}>{t('legalId.completed.title')}</h2>
              <p className={styles.subtitle}>{t('legalId.completed.subtitle')}</p>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className={styles.card}>
              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t('legalId.error.title')}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}

        </main>

        <footer className={styles.footer}>
          <LanguageSelector />
          <div className={styles.footerLinks}>
            <a href="https://www.id.me/about" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.whatIsIdme')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.termsOfService')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.privacyPolicy')}</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
